import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  Button,
  TextField,
  Grid,
  Typography,
  Box,
  IconButton,
  Snackbar,
  Alert,
  CircularProgress,
  FormControlLabel,
  Switch,
} from '@mui/material';
import {
  Save as SaveIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import OriginDataService from '../../services/origin.service';
import { useTranslation } from '../../hooks/useTranslation';

const OriginForm = () => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name_en: '',
    name_es: '',
    code: '',
    is_active: true,
    description_en: '',
    description_es: '',
  });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);
  const { t, language } = useTranslation();

  useEffect(() => {
    if (isEdit) {
      fetchOrigin();
    }
  }, [id, isEdit]);

  const fetchOrigin = async () => {
    setLoading(true);
    try {
      const response = await OriginDataService.get(id);
      const origin = response.data?.data;
      setFormData({
        name_en: origin.name_en || '',
        name_es: origin.name_es || '',
        code: origin.code || '',
        is_active: origin.is_active !== undefined ? origin.is_active : true,
        description_en: origin.description_en || '',
        description_es: origin.description_es || '',
      });
    } catch (error) {
      console.error('Error loading origin:', error);
      showSnackbar(t('origins.errorLoadingOrigin'), 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async () => {
    if (!formData.name_en && !formData.name_es) {
      showSnackbar(t('origins.pleaseFillOneName'), 'error');
      return;
    }

    try {
      setLoading(true);
      if (isEdit) {
        await OriginDataService.update(id, formData);
        showSnackbar(t('origins.originUpdatedSuccess'));
      } else {
        await OriginDataService.create(formData);
        showSnackbar(t('origins.originCreatedSuccess'));
      }
      navigate('/origins');
    } catch (error) {
      showSnackbar(`Error ${isEdit ? 'updating' : 'creating'} origin: ${error.response?.data?.message || error.message}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  if (loading && isEdit) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Card>
      <CardHeader
        title={
          <Box display="flex" alignItems="center" gap={1}>
            <IconButton onClick={() => navigate('/origins')}>
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h5">
              {isEdit ? t('origins.editOrigin') : t('origins.newOrigin')}
            </Typography>
          </Box>
        }
        action={
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSubmit}
            disabled={loading}
          >
            {isEdit ? t('common.update') : t('common.save')} {t('origins.title')}
          </Button>
        }
      />
      <CardContent>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label={t('origins.name') + ' (' + t('common.english') + ')'}
              name="name_en"
              value={formData.name_en}
              onChange={handleInputChange}
              helperText={!formData.name_es && !formData.name_en ? t('origins.nameRequiredHelper') : ''}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label={t('origins.name') + ' (' + t('common.spanish') + ')'}
              name="name_es"
              value={formData.name_es}
              onChange={handleInputChange}
              helperText={!formData.name_en && !formData.name_es ? t('origins.nameRequiredHelper') : ''}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label={t('origins.code') + ' (' + t('common.optional') + ')'}
              name="code"
              value={formData.code}
              onChange={handleInputChange}
              helperText={t('origins.helperText')}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControlLabel
              control={
                <Switch
                  name="is_active"
                  checked={formData.is_active}
                  onChange={handleInputChange}
                />
              }
              label={t('origins.status')}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label={t('origins.description') + ' (' + t('common.english') + ')'}
              name="description_en"
              value={formData.description_en}
              onChange={handleInputChange}
              multiline
              rows={3}
              placeholder="Optional description"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label={t('origins.description') + ' (' + t('common.spanish') + ')'}
              name="description_es"
              value={formData.description_es}
              onChange={handleInputChange}
              multiline
              rows={3}
              placeholder="Descripción opcional"
            />
          </Grid>
        </Grid>
      </CardContent>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Card>
  );
};

export default OriginForm;
